/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export default class PerformanceUtils {
	/**
	 * Returns the performance timing by name
	 * More information:- https://developer.mozilla.org/en-US/docs/Web/API/Performance/getEntriesByName
	 * @param  {[string]} name
	 * @return {Array<Object>}
	 *
	 */
	static getPerformanceTimingByName(name: string) {
		let performanceTimingByName: PerformanceEntryList = [];

		try {
			if (window.performance && window.performance.getEntries()) {
				performanceTimingByName = window.performance.getEntriesByName(name);
			}
		} catch (e) {
			// There was an issue accessing the performance object, returning the default value
		}

		return performanceTimingByName;
	}

	/**
	 * Returns the memory performance
	 * More information:- https://developer.mozilla.org/en-US/docs/Web/API/Performance/memory
	 * @return {Object}
	 */
	static getMemoryPerformance() {
		const memoryPerformance = {};

		try {
			// @ts-ignore
			if (window.performance && window.performance.memory) {
				// @ts-ignore
				memoryPerformance.totalJSHeapSize = window.performance.memory.totalJSHeapSize;
				// @ts-ignore
				memoryPerformance.usedJSHeapSize = window.performance.memory.usedJSHeapSize;
				// @ts-ignore
				memoryPerformance.jsHeapSizeLimit = window.performance.memory.jsHeapSizeLimit;
			}
		} catch (e) {
			// There was an issue accessing the performance object, returning the default value
		}

		return memoryPerformance;
	}

	/**
	 * Returns the navigation performance
	 * @return {Object}
	 */
	static getNavigationPerformance() {
		let navigationPerformance = {};
		let navigationData = {};

		try {
			if (window.performance && window.performance.getEntries()) {
				navigationData = window.performance.getEntriesByType('navigation');
				// @ts-ignore
				if (navigationData && navigationData.length) {
					navigationPerformance = navigationData[0];
				}
			}
		} catch (e) {
			// There was an issue accessing the performance object, returning the default value
		}

		return navigationPerformance;
	}

	/**
	 * Returns the performance data reported by the browser
	 * @return {Object}
	 */
	static getPerformanceData() {
		const performanceData: Record<string, any> = {
			firstContentfulPaint: [],
			firstPaint: [],
			memory: PerformanceUtils.getMemoryPerformance(),
			navigation: PerformanceUtils.getNavigationPerformance(),
			performance: {},
		};

		let firstPaint: PerformanceEntryList;
		let firstContentfulPaint: PerformanceEntryList;

		try {
			if (window.performance && window.performance.getEntries()) {
				performanceData.performance = window.performance;
				firstPaint = window.performance.getEntriesByName('first-paint');
				firstContentfulPaint = window.performance.getEntriesByName('first-contentful-paint');

				if (firstPaint && firstPaint.length > 0) {
					performanceData.firstPaint = firstPaint[0];
				}

				if (firstContentfulPaint && firstContentfulPaint.length > 0) {
					performanceData.firstContentfulPaint = firstContentfulPaint[0];
				}
			}
		} catch (e) {
			// There was an issue accessing the performance object, returning the default value
		}

		return performanceData;
	}
}
